import { LOADING_STATE } from '@/helpers/constants'
import type {
  AddressId,
  ChatMessage,
  ChatRecord,
  GetChatCursor,
  LoadingState,
  SubscribeToChatMessagesParams,
} from '@/helpers/types'
import { dedupePrimitiveArray, sortBy } from '@/helpers/utils'
import { useMainStore } from '@/stores/main'
import { useUserStore } from '@/stores/user'
import type {
  ConversationChatMessage,
  ConversationChatMessagesSubscribeResult,
  ConversationSubscribeResult,
  GetConversationChatMessageResult,
  GetConversationMessagesParams,
  SendConversationMessageParams,
  SendConversationMessageResult,
} from '@signalwire/client'
import { create, type StateCreator } from 'zustand'

const CHATS_PER_PAGE = 15 as const

interface PaginationParams {
  addressId: AddressId
  nextPage: GetConversationChatMessageResult['nextPage'] | undefined
  prevPage: GetConversationChatMessageResult['prevPage'] | undefined
}

interface UpdateChatStoreFromResponseParams {
  addressId: AddressId
  conversationChatMessages: ConversationChatMessage[]
  nextPage: GetConversationChatMessageResult['nextPage'] | undefined
  prevPage: GetConversationChatMessageResult['prevPage'] | undefined
}

interface Actions {
  actions: {
    _addChatIdsToAddressMap: (
      addressId: AddressId,
      chatIds: ChatRecord['id'][],
    ) => void
    _addChatMessagesToChatMessageMap: (chatMessages: ChatRecord[]) => void
    _addSubscriptionToMap: (
      addressId: AddressId,
      subscription: ConversationSubscribeResult,
    ) => void
    _fetchAndUpdateChatMessages: (params: {
      getChatCursor: GetChatCursor
      groupId: AddressId
    }) => Promise<void>
    _setPaginationCursors: (paginationParams: PaginationParams) => void
    _updateChatStoreFromResponse: (
      params: UpdateChatStoreFromResponseParams,
    ) => void
    getChatMessages: (params: {
      addressId: AddressId
      pageSize?: number
    }) => Promise<void>
    getGroupIdForAddress: (addressId: AddressId) => string | undefined
    getNextChatPage: (params: { addressId: AddressId }) => Promise<void>
    getPrevChatPage: (params: { addressId: AddressId }) => Promise<void>
    getSortedChatMessages: (params: {
      addressId: AddressId
      chatMessageMap: ChatMessageMap
    }) => ChatMessage[]
    joinChatConversation: (params: {
      addressId: AddressId
    }) => Promise<{ group_id: string }>
    sendChatMessage: (params: {
      addressId: AddressId
      details?: Record<string, unknown>
      metadata?: Record<string, unknown>
      text: string
    }) => Promise<SendConversationMessageResult>
    subscribeToChatMessages: (
      params: SubscribeToChatMessagesParams,
    ) => Promise<ConversationChatMessagesSubscribeResult>
    unsubscribeChatMessages: (addressId: AddressId) => void
  }
}

type ResourceChatIdMap = Map<AddressId, ChatRecord['id'][]>
type ChatMessageMap = Map<ChatRecord['id'], ChatRecord>
type CursorMap = Map<
  AddressId,
  {
    nextPage: GetConversationChatMessageResult['nextPage'] | undefined
    prevPage: GetConversationChatMessageResult['prevPage'] | undefined
  }
>

// TODO: Include user meta-data for the UI as the message list is initialized and updated
// Colors should be assigned to users from a color list provided by the designer
// The color assignments (and possibly all meta-data) should persist after a refresh
interface State {
  addressIdChatIdMap: ResourceChatIdMap
  addressIdToGroupIdMap: Map<AddressId, string>
  chatHasNextPage: boolean
  chatMessageMap: ChatMessageMap
  cursorMap: CursorMap
  loadingState: LoadingState
  subscriptionsMap: Map<AddressId, ConversationSubscribeResult>
}

type Store = Actions & State

const initialState: State = {
  addressIdChatIdMap: new Map(),
  addressIdToGroupIdMap: new Map(),
  chatHasNextPage: false,
  chatMessageMap: new Map(),
  cursorMap: new Map(),
  loadingState: LOADING_STATE.IDLE,
  subscriptionsMap: new Map(),
}

export const mapChatMessageToRecord = (
  conversationChatMessage: ConversationChatMessage,
) => {
  // use camelCase for keys
  return {
    details: conversationChatMessage.details,
    fromFabricAddressId: conversationChatMessage.from_fabric_address_id,
    groupId: conversationChatMessage.group_id,
    id: conversationChatMessage.id,
    subtype: conversationChatMessage.subtype,
    text: conversationChatMessage.text,
    ts: conversationChatMessage.ts,
    type: conversationChatMessage.type,
    userName: (conversationChatMessage.user_name as string) ?? '',
  } satisfies ChatRecord
}

const stateCreatorFn: StateCreator<Store> = (set, get) => ({
  ...initialState,
  actions: {
    _addChatIdsToAddressMap: (addressId, chatIds) => {
      const { addressIdChatIdMap } = get()

      const map = new Map(addressIdChatIdMap)

      const currentChatIds = map.get(addressId) ?? []

      // remove duplicates and add new chat ids
      const mergedChatIds = dedupePrimitiveArray([
        ...currentChatIds,
        ...chatIds,
      ])

      // clone the map and
      const updatedMap = new Map(map)

      // update the chat ids for the address
      updatedMap.set(addressId, mergedChatIds)

      set({ addressIdChatIdMap: updatedMap })
    },
    _addChatMessagesToChatMessageMap: chatMessages => {
      const { chatMessageMap } = get()

      // clone the map
      const updatedMap = new Map(chatMessageMap)

      // update the chat message map with new chat messages
      chatMessages.forEach(chatMessage => {
        updatedMap.set(chatMessage.id, chatMessage)
      })
      set({ chatMessageMap: updatedMap })
    },

    _addSubscriptionToMap: (addressId, subscription) => {
      const { subscriptionsMap } = get()
      const updatedMap = new Map(subscriptionsMap)

      // if current subscription exists, unsubscribe first
      const currentSubscription = updatedMap.get(addressId)
      if (currentSubscription) {
        currentSubscription.unsubscribe()
      }
      // add the new subscription
      updatedMap.set(addressId, subscription)
      set({ subscriptionsMap: updatedMap })
    },
    _fetchAndUpdateChatMessages: async ({ groupId, getChatCursor }) => {
      const { _updateChatStoreFromResponse } = get().actions
      set({ loadingState: LOADING_STATE.LOADING })

      try {
        // fetch chat messages
        const getMessagesResponse = await getChatCursor()
        console.log('XXXX fetch chat messages response', getMessagesResponse)
        if (!getMessagesResponse) {
          set({ loadingState: LOADING_STATE.ERROR })
          return
        }

        // eslint-disable-next-line @typescript-eslint/unbound-method
        const { data, hasNext, hasPrev, nextPage, prevPage } =
          getMessagesResponse

        _updateChatStoreFromResponse({
          addressId: groupId,
          conversationChatMessages: data,
          nextPage: hasNext ? nextPage : undefined,
          prevPage: hasPrev ? prevPage : undefined,
        })
        set({ loadingState: LOADING_STATE.IDLE })
      } catch (error) {
        set({ loadingState: LOADING_STATE.ERROR })
        console.error('Error fetching chat cursor', error)
        // rethrow
        throw error
      }
    },
    _setPaginationCursors: ({ addressId, nextPage, prevPage }) => {
      const { cursorMap } = get()
      const updatedMap = new Map(cursorMap)

      updatedMap.set(addressId, { nextPage, prevPage })
      set({ cursorMap: updatedMap })
    },
    _updateChatStoreFromResponse: ({
      addressId,
      conversationChatMessages,
      nextPage,
      prevPage,
    }) => {
      const {
        _addChatIdsToAddressMap,
        _addChatMessagesToChatMessageMap,
        _setPaginationCursors,
      } = get().actions
      const { upsertManyUserMetaData } = useUserStore.getState().actions

      // map chat messages
      const chatMessages = conversationChatMessages.map(mapChatMessageToRecord)

      // add chat ids to address map for given address id (if provided)
      if (addressId && Array.isArray(chatMessages)) {
        _addChatIdsToAddressMap(
          addressId,
          chatMessages.map(chatMessage => chatMessage.id),
        )
      }

      // add new chat messages to chat message map
      _addChatMessagesToChatMessageMap(chatMessages)

      // update cursors (if provided)
      if (addressId && (nextPage || prevPage)) {
        // update hasNext flag
        set({ chatHasNextPage: Boolean(nextPage) })
        _setPaginationCursors({ addressId, nextPage, prevPage })
      }

      // update user meta-data map
      upsertManyUserMetaData(
        chatMessages.map(chat => ({
          userId: chat.fromFabricAddressId,
          userName: chat.userName,
        })),
      )
    },

    getChatMessages: async ({ addressId, pageSize = CHATS_PER_PAGE }) => {
      const { client } = useMainStore.getState()
      if (!client) {
        throw new Error('Client not initialized')
      }

      const { loadingState, addressIdToGroupIdMap } = get()
      const { _fetchAndUpdateChatMessages, joinChatConversation } =
        get().actions

      // Get or create group_id for this address
      let groupId = addressIdToGroupIdMap.get(addressId)
      if (!groupId) {
        // Need to join first to get groupId
        const joinResult = await joinChatConversation({ addressId })
        groupId = joinResult.group_id
      }

      // reset cursors, and hasNextPage
      set({
        chatHasNextPage: false,
        cursorMap: new Map(),
      })

      // groupId is required
      const getOptions: GetConversationMessagesParams = {
        groupId,
      }

      // optional fields
      if (pageSize) {
        getOptions.pageSize = pageSize
      }

      if (loadingState !== LOADING_STATE.LOADING) {
        const getChatMessagesFromClient = async () =>
          await client.chat.getMessages(getOptions)

        await _fetchAndUpdateChatMessages({
          getChatCursor: getChatMessagesFromClient,
          groupId: addressId,
        })
      }
    },
    getGroupIdForAddress: (addressId: AddressId) => {
      const { addressIdToGroupIdMap } = get()
      return addressIdToGroupIdMap.get(addressId)
    },
    getNextChatPage: async ({ addressId }) => {
      const { actions, cursorMap, loadingState } = get()
      const { _fetchAndUpdateChatMessages } = actions

      // get the next page cursor
      const nextPage = cursorMap.get(addressId)?.nextPage
      if (!addressId || !nextPage) {
        return
      }

      if (loadingState !== LOADING_STATE.LOADING) {
        await _fetchAndUpdateChatMessages({
          getChatCursor: nextPage,
          groupId: addressId,
        })
      }
    },
    getPrevChatPage: async ({ addressId }) => {
      const { actions, cursorMap, loadingState } = get()
      const { _fetchAndUpdateChatMessages } = actions

      // get the previous page cursor
      const prevPage = cursorMap.get(addressId)?.prevPage
      if (!addressId || !prevPage) {
        return
      }

      if (loadingState !== LOADING_STATE.LOADING) {
        await _fetchAndUpdateChatMessages({
          getChatCursor: prevPage,
          groupId: addressId,
        })
      }
    },
    getSortedChatMessages: ({ addressId, chatMessageMap }) => {
      const { addressIdChatIdMap } = get()

      const chatIds = addressIdChatIdMap.get(addressId ?? '')

      if (!chatIds) {
        return []
      }

      const chatMessages = chatIds
        .map(chatId => chatMessageMap.get(chatId))
        // FIXME: In TS version >5.5 filtering is automatically inferred, remove casting
        .filter(v => v) as ChatMessage[]

      // sort by timestamp
      const sortedChatMessagesAsc = sortBy(chatMessages, 'asc', 'ts')

      return sortedChatMessagesAsc
    },
    joinChatConversation: async ({ addressId }) => {
      const { client } = useMainStore.getState()
      if (!client) {
        throw new Error('Client not initialized')
      }
      const myAddresses = await client.address.getMyAddresses()
      const fromAddressId = myAddresses[0]?.id ?? ''

      const result = await client.chat.join({
        addressIds: [addressId, fromAddressId],
        fromAddressId,
      })

      // Store the groupId mapping
      const { addressIdToGroupIdMap } = get()
      const updatedMap = new Map(addressIdToGroupIdMap)
      updatedMap.set(addressId, result.group_id)
      set({ addressIdToGroupIdMap: updatedMap })

      return result
    },
    sendChatMessage: async ({ addressId, details, metadata, text }) => {
      const { client } = useMainStore.getState()
      if (!client) {
        throw new Error('Client not initialized')
      }

      const { addressIdToGroupIdMap } = get()
      const { joinChatConversation } = get().actions

      // Get or create group_id for this address
      let groupId = addressIdToGroupIdMap.get(addressId)
      if (!groupId) {
        // Need to join first to get groupId
        const joinResult = await joinChatConversation({ addressId })
        groupId = joinResult.group_id
      }

      const fromAddressId = (await client.address.getMyAddresses())[0]?.id || ''

      const messageToSend: SendConversationMessageParams = {
        fromAddressId,
        groupId,
        text,
      }

      // optional fields
      if (details) {
        messageToSend.details ??= details
      }
      if (metadata) {
        messageToSend.metadata ??= metadata
      }

      const sendMessageResponse = await client.chat.sendMessage(messageToSend)

      console.log('XXXX send message response', sendMessageResponse)
      // TODO: update the store with the response?

      return sendMessageResponse
    },
    // FIXME: onMessage callback param should be optional
    subscribeToChatMessages: async ({ addressId, onMessage }) => {
      const { client } = useMainStore.getState()
      if (!client) {
        throw new Error('Client not initialized')
      }

      // Join the Chat Conversation
      // FIXME: Do we need to call join each time we subscribe?
      const { joinChatConversation, _addSubscriptionToMap } = get().actions
      const joinResult = await joinChatConversation({ addressId })

      // Subscribe to incoming Chat messages
      const unsubscribeToChatMessages = await client.chat.subscribe({
        groupId: joinResult.group_id,
        onMessage: event => {
          const { _updateChatStoreFromResponse } = get().actions
          console.log('XXXX onMessage event', event)

          const newChatRecord = {
            ...event,
            text: event.text ?? '',
            // eslint-disable-next-line camelcase
            user_name: event.user_name ?? '',
          } satisfies ConversationChatMessage

          _updateChatStoreFromResponse({
            addressId,
            conversationChatMessages: [newChatRecord],
            nextPage: undefined,
            prevPage: undefined,
          })

          // call the onMessage callback
          onMessage(event)
        },
      })

      // add the subscription to the map
      _addSubscriptionToMap(addressId, unsubscribeToChatMessages)

      // return the unsubscribe function
      return unsubscribeToChatMessages
    },
    unsubscribeChatMessages: addressId => {
      const { subscriptionsMap } = get()
      const subscription = subscriptionsMap.get(addressId)
      if (!subscription) {
        // no-op if subscription does not exist
        return
      }
      // unsubscribe
      subscription.unsubscribe()
      console.log(
        'XXXX: Unsubscribed from chat messages with addressId',
        addressId,
      )
      // remove the subscription and update the map state
      const updatedMap = new Map(subscriptionsMap)
      updatedMap.delete(addressId)

      // update the store
      set({ subscriptionsMap: updatedMap })
    },
  },
})

export const useChatStore = create<Store>()(stateCreatorFn)
export const useChatStoreActions = () => useChatStore.getState().actions

export type { Store as ChatStoreState }
